'use client';

import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { env } from '../../lib/env';
import { ModuleWrapper, type ModuleWrapperProps } from '../module-wrapper';

interface FoursixtyUgcModuleProps {
  siteId: string;
  title: string | null;
  moduleSettings: ModuleWrapperProps | null;
  headingComponent?: ReactNode;
}

export function FoursixtyUgcModuleShared({
  siteId,
  title,
  moduleSettings,
  headingComponent,
}: FoursixtyUgcModuleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!containerRef.current || scriptLoadedRef.current) return;

    // Check if script already exists in the container
    const existingScript = containerRef.current.querySelector(
      'script[src*="foursixty"]'
    );
    if (existingScript) return;

    scriptLoadedRef.current = true;

    const script = document.createElement('script');
    script.src = 'https://foursixty.com/media/scripts/fs.slider.v2.5.js';
    script.defer = true;
    script.setAttribute('data-feed-id', siteId);
    script.setAttribute('data-shopify-add-to-cart', 'false');
    script.setAttribute('data-theme', 'slider_v2_5');
    script.setAttribute('data-cell-size', '25%');
    script.setAttribute('data-for-url', 'true');
    script.setAttribute(
      'data-has-posts-selector',
      '#foursixty-hideable-container'
    );

    containerRef.current.appendChild(script);

    return () => {
      if (script && containerRef.current?.contains(script)) {
        containerRef.current.removeChild(script);
      }
      scriptLoadedRef.current = false;
    };
  }, []);

  return (
    <ModuleWrapper
      topPadding={moduleSettings?.topPadding}
      bottomPadding={moduleSettings?.bottomPadding}
      id="foursixty-hideable-container"
    >
      <script
        type="application/json"
        id="oke-reviews-settings"
      >{`{"subscriberId":"${env.NEXT_PUBLIC_OKENDO_SUBSCRIBER_ID}"}`}</script>
      <section className="fs-section grid gap-8" ref={containerRef}>
        {title && headingComponent}

        <style>
          {`
          /* Hide the container by default */
          #foursixty-hideable-container {
            display: none;
          }

          /* Show the container only when there are actual UGC entries */
          #foursixty-hideable-container:has(.fs-timeline-entry) {
            display: block;
          }

          /* Fallback: also show if Foursixty adds the has-posts indicator */
          #foursixty-hideable-container:has(.fs-has-posts) {
            display: block;
          }

          /* Additional fallback for browsers without :has() support - 
             this uses CSS that only works when timeline entries are present */
          .fs-timeline-entry ~ * #foursixty-hideable-container,
          .fs-timeline-entry + * #foursixty-hideable-container {
            display: block;
          }

          .fs-link-text::after {
       content: "SHOP NOW";
       display: block;
       font-family: inherit;
       max-width: 120px;
       background-color: transparent;
       color: #222;
       border: 1px solid #222;
       padding: 10px;
       margin: 15px auto;
     }

          .fs-has-links::after {
        padding: 5px 7.5px;
        border: 3px solid #fff;
        color: #fff;
        content: "SHOP NOW"
      }

      .fs-has-links:hover:after {
        background-color: #fff;
        color: rgba(30, 30, 30, 0.8)
      }

      .fs-wrapper {
        height: auto
      }

      .fs-entry-container {
        height: 0 !important;
        width: 25% !important;
        padding-top: 25% !important
      }

      .fs-wrapper div.fs-text-container .fs-entry-title, div.fs-detail-title {
        font-family: Asap, sans-serif;
        font-style: normal;
        font-weight: normal;
        font-size: 11px
      }

      div.fs-text-container .fs-entry-date, div.fs-detail-container .fs-post-info, div.fs-wrapper div.fs-has-links::after, .fs-text-product, .fs-overlink-text {
        font-family: Asap, sans-serif;
        font-style: normal;
        font-weight: bold;
        font-size: 14px
      }

      .fs-wrapper div.fs-text-container * {
        color: #fff
      }

      .fs-wrapper div.fs-text-container {
        background-color: rgba(30, 30, 30, 0.8);
        margin: 0px
      }

      div.fs-entry-date {
        display: none
      }

      div.fs-entry-title {
        display: none
      }

      .fs-wrapper div.fs-timeline-entry {
        margin: 6px
      }

      .fs-slider-next-button, .fs-slider-prev-button {
        opacity: 1
      }

      .fs-add-to-cart-enabled .fs-view a, .fs-add-to-cart-enabled .fs-view a:link {
        font-size: 12px;
        font-weight: bold;
      }

      .fs-shopify-add-cart.fs-shopify-add-cart-container {
        max-width: 110px;
        font-size: 11px;
        color: #fff;
        background-color: #74cbb3;
      }

      .fs-post-info {
        font-size: 10px !important;
      }

      .fs-section {
        padding-top: 20px;
        padding-bottom: 20px;
        text-align: center;
      }

      @media only screen and (max-width: 768px) {
        .fs-timeline {
          max-width: calc(100% - 120px);
          margin: auto;
        }

        .fs-slider-next-button, .fs-slider-prev-button {
          opacity: 1 !important;
          display: flex !important;
        }

        .fs-wrapper div.fs-entry-container {
          height: 0;
          width: 100% !important;
          padding-top: 100% !important;
        }
      }
    `}
        </style>
      </section>
    </ModuleWrapper>
  );
}
